import type {
  Consignment,
  CreateConsignmentRequest,
  CreateConsignmentResponse,
  ConsignmentDetail,
  UpdateConsignmentRequest,
  TaxEstimateRequest,
  TaxEstimateResponse,
  ParsedInvoice,
} from './api/types';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.helm.tarifix.xtellar.app';

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
}

async function fetchApi<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');

  let url = `${API_BASE_URL}${path}`;
  if (options.params) {
    const searchParams = new URLSearchParams(options.params);
    url += `?${searchParams.toString()}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error?.message || 'API request failed');
  }

  return data.data;
}

export const api = {
  consignments: {
    list: () => fetchApi<Consignment[]>('/consignments'),
    get: (id: string) => fetchApi<ConsignmentDetail>(`/consignments/${id}`),
    create: (data: CreateConsignmentRequest) => fetchApi<CreateConsignmentResponse>('/consignments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
    update: (id: string, data: UpdateConsignmentRequest) => fetchApi<Consignment>(`/consignments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
    delete: (id: string) => fetchApi<{ deleted: true }>(`/consignments/${id}`, {
      method: 'DELETE',
    }),
  },
  tax: {
    estimate: (data: TaxEstimateRequest) => fetchApi<TaxEstimateResponse>('/tax/estimate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  },
  parseInvoice: (imageUrl: string) => fetchApi<ParsedInvoice>('/parse-invoice', {
    method: 'POST',
    body: JSON.stringify({ image_url: imageUrl }),
  }),
};
